import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ahjApi } from "@/services/ahjService";
import { IAHJ, AHJType } from "@/types/ahj";
import { AHJFormDialog } from "@/components/ahj/AHJFormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Plus, Search, Pencil, Trash2, Loader2, Filter, X, ChevronRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { US_STATES } from "@/data/states";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEffect } from "react";

function useMediaQuery(query: string) {
  const [value, setValue] = useState(false);

  useEffect(() => {
    function onChange(event: MediaQueryListEvent) {
      setValue(event.matches);
    }

    const result = matchMedia(query);
    result.addEventListener("change", onChange);
    setValue(result.matches);

    return () => result.removeEventListener("change", onChange);
  }, [query]);

  return value;
}

const AHJDetailsContent = ({
  ahj,
  onEdit,
  onDelete,
}: {
  ahj: IAHJ;
  onEdit: (ahj: IAHJ) => void;
  onDelete: (ahj: IAHJ) => void;
}) => {
  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 pb-4">
        <div className="space-y-4">
          {ahj.requiredSubmittalProcess && (
            <div>
              <h3 className="font-semibold text-sm mb-2">Submittal Process</h3>
              <div
                className="prose prose-sm max-w-none
                  prose-p:text-muted-foreground prose-p:my-2 prose-p:text-sm
                  prose-ul:my-2 prose-ul:pl-0
                  prose-ol:my-2 prose-ol:pl-0
                  prose-li:text-muted-foreground prose-li:my-1 prose-li:text-sm
                  prose-strong:font-bold prose-strong:text-foreground
                  prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs
                  prose-pre:bg-muted prose-pre:p-3 prose-pre:rounded-md prose-pre:overflow-x-auto prose-pre:text-xs
                  prose-img:rounded-md prose-img:my-2 prose-img:max-w-full prose-img:cursor-pointer prose-img:transition-transform prose-img:hover:scale-105
                  prose-a:text-primary prose-a:underline prose-a:text-sm
                  [&_img]:cursor-pointer [&_img]:max-w-full [&_img]:h-auto
                  [&_ol]:list-decimal [&_ol]:ml-3
                  [&_ul]:list-disc [&_ul]:ml-3"
                dangerouslySetInnerHTML={{
                  __html: ahj.requiredSubmittalProcess,
                }}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.tagName === "IMG") {
                    const img = target as HTMLImageElement;
                    window.open(img.src, "_blank");
                  }
                }}
              />
            </div>
          )}

          {ahj.avgReviewTimeline && (
            <div>
              <h3 className="font-semibold text-sm mb-1">
                Avg Review Timeline
              </h3>
              <p className="text-sm text-muted-foreground">
                {ahj.avgReviewTimeline}
              </p>
            </div>
          )}

          {ahj.expeditedServices && (
            <div>
              <h3 className="font-semibold text-sm mb-1">Expedited Services</h3>
              <p className="text-sm text-muted-foreground">
                {ahj.expeditedServices}
              </p>
            </div>
          )}

          {ahj.expeditedServicesCost && (
            <div>
              <h3 className="font-semibold text-sm mb-1">Cost</h3>
              <p className="text-sm text-muted-foreground">
                {ahj.expeditedServicesCost}
              </p>
            </div>
          )}

          <div>
            <h3 className="font-semibold text-sm mb-2">
              Additional Required Documents
            </h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(ahj.permittingRequirements).map(
                ([key, value]) =>
                  value && (
                    <span
                      key={key}
                      className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium"
                    >
                      {
                        permittingRequirementsLabels[
                          key as keyof typeof permittingRequirementsLabels
                        ]
                      }
                    </span>
                  )
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t px-6 py-4 bg-background mt-auto">
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onEdit(ahj)}
          >
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onDelete(ahj)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>
    </>
  );
};

const permittingRequirementsLabels: Record<string, string> = {
  slabUpliftTest: "Slab Uplift Test",
  slabCoreTest: "Slab Core Test",
  adaPlans: "ADA Plans",
  photometricPlans: "Photometric Plans",
  egressPlans: "Egress Plans",
  smokeVentInspections: "Smoke Vent Inspections",
  fpeStamp: "FPE Stamp",
  weightSigns: "Weight Signs",
  flueKeepersPalletStops: "Flue Keepers Pallet Stops",
  keepClearsStickers: "Keep Clears Stickers",
  fireSignsCommoditySigns: "Fire Signs/Commodity Signs",
};

export default function AHJDatabase() {
  const queryClient = useQueryClient();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterState, setFilterState] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAHJ, setEditingAHJ] = useState<IAHJ | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ahjToDelete, setAhjToDelete] = useState<IAHJ | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [selectedAHJ, setSelectedAHJ] = useState<IAHJ | null>(null);
  const [detailsSheetOpen, setDetailsSheetOpen] = useState(false);

  // Fetch AHJs
  const { data, isLoading } = useQuery({
    queryKey: ["ahjs", filterState, filterType, searchTerm],
    queryFn: () =>
      ahjApi.getAHJs({
        state: filterState === "all" ? undefined : filterState,
        type: filterType === "all" ? undefined : filterType,
        search: searchTerm || undefined,
      }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: ahjApi.deleteAHJ,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ahjs"] });
      toast.success("AHJ deleted successfully");
      setDeleteDialogOpen(false);
      setAhjToDelete(null);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err?.response?.data?.error || "Failed to delete AHJ");
    },
  });

  const handleOpenDialog = (ahj?: IAHJ) => {
    setEditingAHJ(ahj || null);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingAHJ(null);
  };

  const handleDelete = (ahj: IAHJ) => {
    setAhjToDelete(ahj);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (ahjToDelete) {
      deleteMutation.mutate(ahjToDelete._id);
    }
  };

  const handleSelectAHJ = (ahj: IAHJ) => {
    setSelectedAHJ(ahj);
    setDetailsSheetOpen(true);
  };

  const handleEditFromSheet = (ahj: IAHJ) => {
    setDetailsSheetOpen(false);
    handleOpenDialog(ahj);
  };

  const handleDeleteFromSheet = (ahj: IAHJ) => {
    setDetailsSheetOpen(false);
    handleDelete(ahj);
  };

  // Group AHJs by type
  const groupedAHJs = data?.ahjs.reduce((acc, ahj) => {
    if (!acc[ahj.type]) acc[ahj.type] = [];
    acc[ahj.type].push(ahj);
    return acc;
  }, {} as Record<string, IAHJ[]>);

  return (
    <div className="container max-w-7xl py-8 space-y-8 animate-in fade-in duration-500">
      {/* Desktop Header */}
      <div className="hidden md:flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">AHJ Database</h1>
          <p className="text-muted-foreground">
            Manage Authority Having Jurisdiction information and requirements.
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="btn-gradient shadow-md hover:shadow-lg transition-all">
          <Plus className="w-4 h-4 mr-2" />
          Add AHJ
        </Button>
      </div>

      {/* Mobile Header with Actions */}
      <div className="md:hidden space-y-4">
        <h1 className="text-2xl font-bold tracking-tight">AHJ Database</h1>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-background/50"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setFilterDrawerOpen(true)}
            className="shrink-0"
          >
            <Filter className="w-4 h-4" />
          </Button>
          <Button onClick={() => handleOpenDialog()} size="icon" className="shrink-0 btn-gradient">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Desktop Filters */}
      <Card className="hidden md:block border-border/50 shadow-sm">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-background/50 focus:bg-background transition-colors"
              />
            </div>
            <Select value={filterState} onValueChange={setFilterState}>
              <SelectTrigger className="bg-background/50 focus:bg-background transition-colors">
                <SelectValue placeholder="Filter by state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {US_STATES.map((state) => (
                  <SelectItem key={state.value} value={state.value}>
                    {state.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="bg-background/50 focus:bg-background transition-colors">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value={AHJType.CITY}>City</SelectItem>
                <SelectItem value={AHJType.COUNTY}>County</SelectItem>
                <SelectItem value={AHJType.SPECIAL}>Special</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* AHJs List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-8">
          {groupedAHJs &&
            Object.entries(groupedAHJs).map(([type, ahjs]) => (
              <div key={type} className="space-y-4">
                {/* Desktop Card View */}
                <Card className="hidden md:block border-border/50 shadow-sophisticated highlight-top overflow-hidden">
                  <CardHeader className="bg-muted/30 border-b border-border/50 pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="capitalize text-xl">{type}s</CardTitle>
                      <span className="text-sm text-muted-foreground bg-background/50 px-2 py-1 rounded-md border border-border/50">
                        {ahjs.length} entries
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-border/50">
                      {ahjs.map((ahj) => (
                        <div
                          key={ahj._id}
                          className="p-6 hover:bg-muted/30 transition-colors group cursor-pointer"
                          onClick={() => handleSelectAHJ(ahj)}
                        >
                          <div className="flex items-start justify-between gap-6">
                            <div className="space-y-3 flex-1">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                              <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors">
                                {ahj.name}
                              </h3>
                              <span className="text-sm text-muted-foreground bg-muted px-2 py-0.5 rounded">
                                {ahj.state}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                              {ahj.requiredSubmittalProcess && (
                                <div className="sm:col-span-2 bg-muted/30 p-3 rounded-md border border-border/50">
                                  <span className="font-medium text-foreground/80 block mb-1">
                                    Submittal Process
                                  </span>
                                  <div 
                                    className="prose prose-sm max-w-none 
                                      prose-p:text-muted-foreground prose-p:my-1
                                      prose-ul:my-1 prose-ul:pl-4 prose-ul:list-inside
                                      prose-ol:my-1 prose-ol:pl-4 prose-ol:list-inside
                                      prose-li:text-muted-foreground prose-li:my-0.5
                                      prose-strong:font-semibold prose-strong:text-foreground
                                      prose-a:text-primary prose-a:no-underline hover:prose-a:underline"
                                    dangerouslySetInnerHTML={{ __html: ahj.requiredSubmittalProcess }}
                                    onClick={(e) => {
                                      const target = e.target as HTMLElement;
                                      if (target.tagName === 'IMG') {
                                        const img = target as HTMLImageElement;
                                        window.open(img.src, '_blank');
                                      }
                                    }}
                                  />
                                </div>
                              )}
                              {ahj.avgReviewTimeline && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-foreground/80">
                                    Avg Review Timeline:
                                  </span>
                                  <span className="text-muted-foreground">
                                    {ahj.avgReviewTimeline}
                                  </span>
                                </div>
                              )}
                              {ahj.expeditedServices && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-foreground/80">
                                    Expedited Services:{" "}
                                  </span>
                                  <span className="text-muted-foreground">
                                    {ahj.expeditedServices}
                                  </span>
                                </div>
                              )}
                              {ahj.expeditedServicesCost && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-foreground/80">Cost: </span>
                                  <span className="text-muted-foreground">
                                    {ahj.expeditedServicesCost}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-border/50">
                              <span className="font-medium text-sm text-foreground/80">Additional Required Documents: </span>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(ahj.permittingRequirements).map(
                                  ([key, value]) =>
                                    value && (
                                      <span
                                        key={key}
                                        className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium border border-primary/20"
                                      >
                                        {
                                          permittingRequirementsLabels[
                                            key as keyof typeof permittingRequirementsLabels
                                          ]
                                        }
                                      </span>
                                    )
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col gap-2 shrink-0 ml-4">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDialog(ahj);
                              }}
                              className="hover:bg-primary/10 hover:text-primary transition-colors"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(ahj);
                              }}
                              className="hover:bg-destructive/10 hover:text-destructive transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Mobile List View */}
              <div className="md:hidden space-y-3">
                <div className="flex items-center justify-between px-1">
                  <h2 className="text-lg font-semibold capitalize text-foreground">{type}s</h2>
                  <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full">{ahjs.length} entries</span>
                </div>
                <div className="space-y-3">
                  {ahjs.map((ahj) => (
                    <div
                      key={ahj._id}
                      className="border border-border/50 rounded-lg p-4 bg-card shadow-sm hover:shadow-md transition-all cursor-pointer active:scale-[0.99]"
                      onClick={() => handleSelectAHJ(ahj)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0 space-y-1">
                          <h3 className="font-semibold text-base text-foreground truncate">
                            {ahj.name}
                          </h3>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="bg-muted px-1.5 py-0.5 rounded">{ahj.state}</span>
                            {ahj.avgReviewTimeline && (
                              <>
                                <span>•</span>
                                <span>{ahj.avgReviewTimeline}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground/50 shrink-0 ml-2" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            ))}

          {data?.ahjs.length === 0 && (
            <Card>
              <CardContent className="py-8 md:py-12 text-center text-muted-foreground text-sm md:text-base">
                No AHJs found. Click "Add AHJ" to create one.
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <AHJFormDialog
        open={isDialogOpen}
        onOpenChange={handleDialogClose}
        editingAHJ={editingAHJ}
      />

      {/* Mobile Filter Drawer */}
      <Drawer open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Filters</DrawerTitle>
            <DrawerDescription>
              Filter AHJs by name, state, and type
            </DrawerDescription>
          </DrawerHeader>
          <div className="p-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterState} onValueChange={setFilterState}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {US_STATES.map((state) => (
                  <SelectItem key={state.value} value={state.value}>
                    {state.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value={AHJType.CITY}>City</SelectItem>
                <SelectItem value={AHJType.COUNTY}>County</SelectItem>
                <SelectItem value={AHJType.SPECIAL}>Special</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="outline">Close</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Details View (Dialog on Desktop, Sheet on Mobile) */}
      {isDesktop ? (
        <Dialog open={detailsSheetOpen} onOpenChange={setDetailsSheetOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
            <DialogHeader className="px-6 pt-6 pb-4 text-left border-b shrink-0">
              <DialogTitle className="text-left text-xl">
                {selectedAHJ?.name}
              </DialogTitle>
              <DialogDescription className="text-left">
                {selectedAHJ?.state} • {selectedAHJ?.type}
              </DialogDescription>
            </DialogHeader>
            {selectedAHJ && (
              <AHJDetailsContent
                ahj={selectedAHJ}
                onEdit={handleEditFromSheet}
                onDelete={handleDeleteFromSheet}
              />
            )}
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={detailsSheetOpen} onOpenChange={setDetailsSheetOpen}>
          <SheetContent side="bottom" className="h-[85vh] flex flex-col p-0">
            <SheetHeader className="px-6 pt-6 pb-4 text-left border-b">
              <SheetTitle className="text-left">{selectedAHJ?.name}</SheetTitle>
              <SheetDescription className="text-left">
                {selectedAHJ?.state} • {selectedAHJ?.type}
              </SheetDescription>
            </SheetHeader>

            {selectedAHJ && (
              <AHJDetailsContent
                ahj={selectedAHJ}
                onEdit={handleEditFromSheet}
                onDelete={handleDeleteFromSheet}
              />
            )}
          </SheetContent>
        </Sheet>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the AHJ "{ahjToDelete?.name}". This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setAhjToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
